<template>
  <div
    class="SearchGalleryElastic">

    <FilterBar
      class="SearchGalleryElastic_FilterBar"
      :title="title"
      :filters="computedFilters"
      :is-loading="isLoading"
      :value="activeFilters"
      @filter-input="handleFilterInput"
      @clear-group="handleClearFilterGroup"
    />

    <FilterMenu
      :showing="showMobileFilterMenu"
      :filters="computedFilters"
      :value="activeFilters"
      @filter-input="handleFilterInput"
      @close="showMobileFilterMenu = false"
    />

    <div
      class="SearchGalleryElastic_Gallery">
      <SearchGalleryHeader
        class="SearchGalleryElastic_SearchGalleryHeader"
        :search-text="searchText"
        :is-loading="isLoading"
        :is-searching="isSearching"
        :active-filters="activeFilters"
        :results-text="computedResultsText"
        :sort-options="sortOptions"
        :sort-value="sortValue"
        @search="handleSearch"
        @filter-input="handleFilterInput"
        @filter-menu-btn-click="showMobileFilterMenu = true"
        @sort="handleSort"
      />

      <SearchGalleryResults
        class="SearchGalleryElastic_SearchGalleryResults"
        ref="searchGalleryResults"
        :results="filteredApps"
        :is-loading="isLoading"
        :is-paginating="isPaginating"
        :is-logos-square="isLogosSquare"
        @click-result="handleResultClick"
        @paginate="handlePaginate"
      />
    </div>

  </div>
</template>

<script>
import { orderBy } from 'lodash';
import FilterBar from '@/components/FilterBar.vue';
import FilterMenu from '@/components/FilterMenu.vue';
import SearchGalleryHeader from '@/components/SearchGalleryHeader.vue';
import SearchGalleryResults from '@/components/SearchGalleryResults.vue';

export default {
  name: 'SearchGalleryElastic',

  components: {
    FilterBar,
    FilterMenu,
    SearchGalleryHeader,
    SearchGalleryResults,
  },

  data() {
    return {
      showMobileFilterMenu: false,
    };
  },

  props: {
    title:         { type: String,  default: null },
    apps:          { type: Array,   default: () => [] },
    isLoading:     { type: Boolean, default: true },
    isPaginating:  { type: Boolean, default: false },
    isSearching:   { type: Boolean, default: false },
    filterMap:     { type: Array,   required: true },
    filters:       { type: Array,   default: () => [] },
    searchText:    { type: String,  default: '' },
    activeFilters: { type: Array,   default: () => [] },
    sortOptions:   { type: Array,   default: null },
    sortValue:     { type: Object,  default: null },
    resultsCount:  { type: Number,  default: 0 },
    isLogosSquare: { type: Boolean, default: true },
    isShowingAllResults: { type: Boolean, default: true },
  },

  computed: {
    computedFilters() {
      const mappedFilters = this.filters.map((filter) => {
        const mappedFilter = this.filterMap.find((mf) => mf.key === filter.key);
        return {
          key: filter.key,
          label: mappedFilter ? mappedFilter.label : filter.label,
          searchBar: mappedFilter ? mappedFilter.searchBar : false,
          searchPlaceholder: mappedFilter ? mappedFilter.searchPlaceholder : '',
          showCount: mappedFilter ? mappedFilter.showCount : 5,
          priority: mappedFilter ? mappedFilter.priority : 0,
          filters: filter.values || [],
        };
      });

      return orderBy(
        mappedFilters,
        'priority',
        'desc',
      );
    },

    computedResultsText() {
      if (this.isLoading) return 'Loading Results';
      if (this.isShowingAllResults) return 'Displaying All Results';
      return `Displaying ${this.resultsCount} Results`;
    },

    filteredApps() {
      if (this.isLoading) {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
          .map((i) => ({ appstore_id: `placeholder${i}` }));
      }

      return this.apps;
    },
  },

  methods: {
    handleResultClick(app) {
      this.$emit('click-result', app);
    },

    handleSearch(searchText) {
      this.$emit('search', searchText.trim());
    },

    handleSort(sortValue) {
      this.$emit('sort', sortValue);
    },

    handlePaginate() {
      this.$emit('paginate');
    },

    handleFilterInput(groupKey, filter) {
      this.$analytics.trackLink({
        position: `app-filter: ${groupKey}`,
        name: `${filter.label} ${([...this.activeFilters].filter((f) => f.value === filter.value).length > 0) ? 'unchecked' : 'checked'}`,
        path: this.$route.path,
      });

      let newValue = [...this.activeFilters].filter((f) => (
        `${f.groupKey}${f.value}` !== `${groupKey}${filter.value}`
      ));

      if (this.activeFilters.length === newValue.length) {
        newValue = [
          ...this.activeFilters,
          { groupKey, label: filter.label, value: filter.value },
        ];
      }

      this.$emit('filter', newValue);
    },

    handleClearFilterGroup(groupKey) {
      const newActiveFiltersValue = this.activeFilters.filter((f) => (f.groupKey !== groupKey));

      this.$emit('filter', newActiveFiltersValue);
    },

    scrollToTopOfResults() {
      this.$refs.searchGalleryResults.scrollToTopOfResults();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

$filter-bar-width: 250px;

.SearchGalleryElastic {
  position: relative;
  height: 100%;
}

.SearchGalleryElastic_Gallery {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: $filter-bar-width;
  flex-direction: column;
}

.SearchGalleryElastic_SearchGalleryHeader {
  padding: 24px 32px 16px 32px;
}

.SearchGalleryElastic_SearchGalleryResults {
  padding: 0 20px 48px 20px;
}

@media only screen and (max-width: 640px) {
  .SearchGalleryElastic_FilterBar {
    display: none;
  }

  .SearchGalleryElastic_Gallery {
    left: 0 !important;
  }

  .SearchGalleryElastic_SearchGalleryHeader {
    padding: 16px 16px 8px 16px !important;
  }

  .SearchGalleryElastic_SearchGalleryResults {
    padding: 0 4px 32px 4px !important;
  }
}
</style>
